<script lang="ts">
    import { sendUIInteractionAnalyticsEvent } from "@app/services/analytics/analytics-send-event";
    import { COMMON__noSuchLanguage } from "@app/services/i18n.messages";

    import TmemFragment from "./TmemFragment.svelte";

    import type { Author } from "./TmemState";
    import {
        setInitial,
        loadNextPage,
        setTmemMode,
        tmemState,
        toggleExcludeAuthor,
        resetAuthorFilter,
        toggleIncludeAuthor,
        updateTmemStem,
    } from "./TmemState";
    import { findElementMatching } from "./TmemUtils";

    function deactivateModeButton(id: string) {
        const btn = document.getElementById(id);
        if (btn) {
            btn.classList.remove("button-xs__active");
        }
    }

    function activateModeButton(btnId: string) {
        deactivateModeButton("tmem__match__exact");
        deactivateModeButton("tmem__match__all");
        deactivateModeButton("tmem__match__any");

        document.getElementById(btnId).classList.add("button-xs__active");
    }

    let forceReload = false;
    function onContainerClick(event: Event) {
        let button: HTMLButtonElement | null = findElementMatching(
            event.target as HTMLElement,
            "button"
        ) as HTMLButtonElement;

        if (!button) {
            return;
        }
        if (button.id === "tmem__more") {
            loadNextPage();
            button.parentNode.removeChild(button);
            sendUIInteractionAnalyticsEvent({
                action: "tmem-more",
            });
        } else if (
            button.id === "tmem__match__exact" ||
            button.dataset.action == "match_exact"
        ) {
            activateModeButton("tmem__match__exact");
            setTmemMode("EXACT");
            sendUIInteractionAnalyticsEvent({
                action: "tmem-match-exact",
            });
        } else if (
            button.id === "tmem__match__all" ||
            button.dataset.action == "match_all"
        ) {
            setTmemMode("MUST");
            activateModeButton("tmem__match__all");
            sendUIInteractionAnalyticsEvent({
                action: "tmem-match-all",
            });
        } else if (
            button.id === "tmem__match__any" ||
            button.dataset.action == "match_any"
        ) {
            setTmemMode("MAY");
            activateModeButton("tmem__match__any");
            sendUIInteractionAnalyticsEvent({
                action: "tmem-match-any",
            });
        } else if (button.dataset.action == "stem") {
            document.getElementById("tmem__stem").click();
        } else if (button.dataset.action == "tmem_reload") {
            forceReload = true;
        } else if (button.dataset.action == "include") {
            let authorId = button.dataset.id;
            let authorName = button.dataset.name;
            onAuthorClick(false, { name: authorName, id: authorId });
        } else if (button.dataset.action == "exclude") {
            let authorId = button.dataset.id;
            let authorName = button.dataset.name;
            onAuthorClick(true, { name: authorName, id: authorId });
        } else if (button.dataset.action == "author_filter_off") {
            resetAuthorFilter();
        }
    }

    // set initial store values
    const excludedAuthorsInitial: Author[] = [
        ...document.querySelectorAll("#authors_excluded button"),
    ].map((x) => ({
        id: x.getAttribute("data-id"),
        name: x.getAttribute("data-name"),
    }));
    const includedAuthorsInitial: Author[] = [
        ...document.querySelectorAll("#authors_included button"),
    ].map((x) => ({
        id: x.getAttribute("data-id"),
        name: x.getAttribute("data-name"),
    }));

    const stemToggle = document.getElementById("tmem__stem");

    const stemInitial: boolean = stemToggle ? (stemToggle as HTMLInputElement).checked : false;
    setInitial(includedAuthorsInitial, excludedAuthorsInitial, stemInitial);

    const container = document.getElementById("examples");
    container.addEventListener("click", onContainerClick);
    if (stemToggle) {
        stemToggle.addEventListener("change", () => {
            updateTmemStem(
                (document.getElementById("tmem__stem") as HTMLInputElement)
                    .checked
            );
        });
        sendUIInteractionAnalyticsEvent({
            action: "tmem-stem",
        });
    }

    interface PageToRender {
        key: string;
        page: number;
    }
    let pages: PageToRender[] = [];
    $: {
        const firstExamples = document.getElementById("tmem_first_examples");
        if ((forceReload || $tmemState.stemOrModeChanged) && firstExamples) {
            firstExamples.parentNode.removeChild(firstExamples);
        }

        const getPageToRender = (page: number, keySuffix: string = "") => ({
            key: `${$tmemState.mode}_${
                $tmemState.stem
            }_${$tmemState.includedAuthors
                .map((author) => author.id)
                .join(",")}_${$tmemState.excludedAuthors
                .map((author) => author.id)
                .join(",")}_${page}_${keySuffix}`,
            page,
        });
        pages = [];
        if (forceReload || $tmemState.stemOrModeChanged) {
            pages.push(
                getPageToRender(1, forceReload ? Math.random() + "" : "")
            );
            forceReload = false;
        }
        for (let i = 2; i <= $tmemState.page; i++) {
            pages.push(getPageToRender(i));
        }
    }

    function onAuthorClick(excluded: boolean, author: Author) {
        if (excluded) {
            toggleExcludeAuthor(author);
        } else {
            toggleIncludeAuthor(author);
        }
    }
</script>

{#each pages as page (page.key)}
    <TmemFragment page={page.page} />
{/each}
